import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import createCompanyApi from '@/api/post/company/create-company';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import Loader from '@/components/loader/loader-component';
import { useMutation } from '@/hooks/use-api';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import { removeUndefinedProperties } from '@/utils/utils';
import CompanyForm from './company-form';
import Styled from './company-new-style';

/**
 * create new company
 * @returns {JSX.Element}
 */
const CompanyNew = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [defaults, setDefaults] = useState({});

  const [createCompany, { loading: createCompanyLoading }] =
    useMutation(createCompanyApi);

  const handleCompanyCreate = (submitValues) => {
    createCompany(removeUndefinedProperties(submitValues))
      .then(({ data }) => {
        if (state?.userForm) {
          navigate('/app/user-management/users/new', {
            state: { companyId: data?.id },
          });
        } else {
          navigate('/app/company-management');
        }
        return Alert({
          icon: 'success',
          text: 'Company created successfully',
        });
      })
      .catch(() =>
        Alert({
          icon: 'error',
          text: 'There was a problem creating the company',
        }),
      );
  };

  const submitForm = (values) => {
    setDefaults(values);
    const submitValues = { ...values };
    const { suppliers, selectedSuppliers } = values;
    if (suppliers) {
      submitValues.suppliers = {
        add:
          selectedSuppliers?.map((supplier) => ({
            supplierId: supplier.supplierId,
            ueEndpoint: supplier.ueEndpoint || null,
          })) || [],
      };
    }

    if (submitValues?.whiteLabelling?.logo?.fileName) {
      const { file, fileName, fileType } = submitValues.whiteLabelling.logo;

      uploadToTempS3Base64(file, fileName, fileType).then((imageData) => {
        const submitValuesWithLogo = {
          ...submitValues,
          whiteLabelling: {
            ...submitValues?.whiteLabelling,
            logo: imageData.url,
            logo_id: imageData.id,
          },
        };
        handleCompanyCreate(submitValuesWithLogo);
      });
    } else {
      handleCompanyCreate(submitValues);
    }
  };

  if (createCompanyLoading) {
    return <Loader loading />;
  }

  return (
    <Styled.Main>
      <FormWrapper defaultValues={defaults} onSubmit={submitForm}>
        <CompanyForm title type="create" isLoading={false} />
        <SubmitCard
          padding="0"
          primaryButtonTitle="Save"
          primaryButtonType="submit"
          primaryButtonWidth="112px"
          primaryPermission={['company.create']}
          secondaryButtonType="button"
          secondaryButtonWidth="112px"
          isLoading={createCompanyLoading}
          secondaryButtonTitle="Cancel"
          submitClickSecondary={() => {
            navigate('/app/company-management');
          }}
        />
      </FormWrapper>
    </Styled.Main>
  );
};
export default CompanyNew;
